import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@apollo/client';
import { useTable, useSortBy, useExpanded, usePagination } from 'react-table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import dayjs from 'dayjs';
import classNames from 'classnames';

import { useUserRestrictions } from '../../auth/user-restrictions';
import { useTeamInfo } from '../../team-id-context';
import { useNotifications } from '../../notifications';
import { GET_EXTRACTED_DATA_BY_TEAM } from '../../api/queries_v2';
import { GET_EXTRACTED_TERMS_FIELD_METADATA } from '../../api/queries/extractedTerms';
import { ContentHeader } from '../../components/private-layout';
import { ContentBody } from '../../components/private-layout/ContentBody';
import SearchInput from '../../components/search-input';
import LoadingSpinner from '../../components/loading-spinner';
import SubscriptionRequiredModal from '../modals/subscriptionModal/SubscriptionRequiredModal';
import { ExtractedTermsSlideout } from '../../components/extractedTermsSlideout';
import { FilterSlideout } from '../../components/extractedTermsFilter';
import { LinkAmendmentModal } from '../../components/linkAmendmentModal';
import { Table } from '../../components/table';
import { ExtractedTermsFilterInput } from '../../types/extractedTerms';
import { DropdownToggle, DropdownBody, DropdownItem } from '../../components/dropdown';
import { Checkbox } from '../../components/checkbox-or-radio-field';

import { ReactComponent as DatabaseIcon } from '../../shared/icons/database-icon.svg';
import { ReactComponent as FilterIcon } from '../../shared/icons/filter-icon.svg';
import { MobilePagination } from '../../components/mobile-layout/MobilePagination';

// Extracted utilities and configurations
import { Document, TableRow, ColumnConfig } from './config/types';
import { getColumnConfigs } from './config/columnDefinitions';
import { extractValue } from './utils/dataFormatters';
import { exportToCSV } from './utils/csvExport';
import { generateColumnsFromMetadata, sortColumnsForDropdown, FieldMetadataMap } from './utils/fieldMetadataToColumns';
import { DocumentNameCell } from './config/cellRenderers';

import styles from './DataPage.module.scss';

const DataPage = () => {
  const { teamId } = useTeamInfo();
  const { canCreateTemplates, restrictionsLoading } = useUserRestrictions();
  const [, { error: notifyError }] = useNotifications();

  // State - ALL hooks must be called unconditionally
  const [searchText, setSearchText] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const [selectedDocumentType, setSelectedDocumentType] = useState<'lease' | 'psa'>('lease'); // Default to lease
  const [activeFilters, setActiveFilters] = useState<ExtractedTermsFilterInput[]>([]);
  const [showFilterSlideout, setShowFilterSlideout] = useState(false);
  const [showLinkAmendmentModal, setShowLinkAmendmentModal] = useState(false);
  const [amendmentToLink, setAmendmentToLink] = useState<{ id: string; title: string; dealId: string; documentId: string } | null>(null);

  // GraphQL queries - must be called unconditionally
  // Query 1: Field metadata for dynamic column generation
  const { data: fieldMetadataData, loading: metadataLoading } = useQuery(
    GET_EXTRACTED_TERMS_FIELD_METADATA,
    {
      variables: { documentType: selectedDocumentType },
      skip: !teamId || !canCreateTemplates || !selectedDocumentType,
    }
  );

  // Memoize fieldMetadata to prevent unnecessary re-renders
  const fieldMetadata: FieldMetadataMap = useMemo(
    () => fieldMetadataData?.extractedTermsFieldMetadata || {},
    [fieldMetadataData]
  );

  // Query 2: Document data - use network-only to always fetch fresh data when page loads
  const { loading, data, error } = useQuery(GET_EXTRACTED_DATA_BY_TEAM, {
    variables: {
      teamId,
      documentType: selectedDocumentType,
      filters: activeFilters.length > 0 ? activeFilters : undefined,
    },
    skip: !teamId || !canCreateTemplates,
    fetchPolicy: 'network-only', // Always fetch fresh data, don't use cache
  });

  // Compute documents unconditionally
  const documents: Document[] = data?.extractedDataByTeam?.documents || [];

  // Update selectedDocument when documents data changes (after refetch)
  // This ensures the slideout shows fresh data after link/unlink operations
  React.useEffect(() => {
    if (selectedDocument && documents.length > 0) {
      // Find the updated version of the selected document
      const updatedDoc = documents.find(doc => doc.id === selectedDocument.id);

      // Only update if the data actually changed (prevent infinite loop)
      // Compare by reference first, then by extracted term data if present
      if (updatedDoc && updatedDoc !== selectedDocument) {
        const hasExtractedTermChanged =
          updatedDoc.extractedDocumentTerm?.linkStatus !== selectedDocument.extractedDocumentTerm?.linkStatus ||
          updatedDoc.extractedDocumentTerm?.anchorDocument?.id !== selectedDocument.extractedDocumentTerm?.anchorDocument?.id;

        if (hasExtractedTermChanged) {
          setSelectedDocument(updatedDoc);
        }
      }
    }
  }, [documents]); // Only watch documents array, not selectedDocument

  // Stable callback for handling row clicks
  const handleRowClick = React.useCallback((doc: Document) => {
    setSelectedDocument(doc);
  }, []);

  // Handle opening link amendment modal
  const handleLinkAmendment = React.useCallback(() => {
    if (selectedDocument) {
      setAmendmentToLink({
        id: selectedDocument.extractedDocumentTerm?.id || '',
        title: selectedDocument.title,
        dealId: selectedDocument.deal.id,
        documentId: selectedDocument.id,
      });
      setShowLinkAmendmentModal(true);
    }
  }, [selectedDocument]);

  // Filter documents based on search
  const filteredDocuments = useMemo(() => {
    if (!searchText) return documents;

    const searchLower = searchText.toLowerCase();
    return documents.filter((doc) => {
      // Search document title
      if (doc.title.toLowerCase().includes(searchLower)) return true;

      // Search deal title
      if (doc.deal.title.toLowerCase().includes(searchLower)) return true;

      // Search extracted terms
      const termsData = doc.extractedDocumentTerm?.termsData;
      if (termsData) {
        // Search tenant/buyer name
        const tenant = termsData?.parties?.tenant_name || termsData?.parties?.buyer_name;
        if (tenant && tenant.toLowerCase().includes(searchLower)) return true;

        // Search landlord/seller name
        const landlord = termsData?.parties?.landlord_name || termsData?.parties?.seller_name;
        if (landlord && landlord.toLowerCase().includes(searchLower)) return true;

        // Search property address
        const address = termsData?.property?.address;
        if (address && address.toLowerCase().includes(searchLower)) return true;
      }

      return false;
    });
  }, [documents, searchText]);

  // Stable onRowClick handler
  const handleTableRowClick = React.useCallback((row: any) => {
    // row.original contains our TableRow data which has originalDocument
    const doc = row.original?.originalDocument || row.originalDocument;
    if (doc) {
      setSelectedDocument(doc);
    }
  }, []);

  // Prepare table data with subRows for amendments
  const tableData: TableRow[] = useMemo(() => {
    return filteredDocuments.map((doc) => {
      const termsData = doc.extractedDocumentTerm?.termsData;
      const hasAmendments = doc.extractedDocumentTerm?.hasAmendments;
      const linkedAmendments = doc.extractedDocumentTerm?.linkedAmendments || [];

      // Prepare sub-rows for amendments
      const subRows = linkedAmendments.map((amendment) => {
        const amendmentTermsData = amendment.termsData;
        const amendmentTitle = amendment.document.title;
        const amendmentDocType = amendment.documentType || '-';
        const amendmentClassification = amendment.classification || '-';
        const amendmentTenantBuyer = extractValue(amendmentTermsData, 'tenant_name') || extractValue(amendmentTermsData, 'buyer_name');
        const amendmentLandlordSeller = extractValue(amendmentTermsData, 'landlord_name') || extractValue(amendmentTermsData, 'seller_name');
        const amendmentPropertyAddress = extractValue(amendmentTermsData, 'property_address');
        const amendmentSquareFeet = extractValue(amendmentTermsData, 'rentable_square_feet') || extractValue(amendmentTermsData, 'total_square_feet');
        const amendmentExtractedDate = amendment.effectiveOn ? dayjs(amendment.effectiveOn).format('MMM D, YYYY') : '-';

        // Create amendment document reference
        const amendmentDoc: Document = {
          id: amendment.document.id,
          title: amendment.document.title,
          status: doc.status,
          deal: doc.deal,
          extractedDocumentTerm: {
            id: amendment.id,
            documentType: amendment.documentType,
            classification: amendment.classification,
            linkStatus: null,
            effectiveOn: amendment.effectiveOn,
            termsData: amendment.termsData,
            termsExtractedAt: doc.extractedDocumentTerm.termsExtractedAt,
            updatedAt: doc.extractedDocumentTerm.updatedAt,
            isAmendment: true,
            isPendingLink: false,
            hasAmendments: false,
            anchorDocument: {
              id: doc.id,
              title: doc.title,
            },
            linkedAmendments: [],
          },
        };

        return {
          id: amendment.document.id,
          title: amendmentTitle,
          // Removed 'deal' - not shown in table columns
          // Removed 'documentType' - not shown in table columns
          tenantBuyer: amendmentTenantBuyer,
          landlordSeller: amendmentLandlordSeller,
          propertyAddress: amendmentPropertyAddress,
          squareFeet: amendmentSquareFeet,
          extractedDate: amendmentExtractedDate,
          isAmendment: true,
          effectiveOn: amendment.effectiveOn,
          originalDocument: amendmentDoc,
          onRowClick: handleTableRowClick,
        };
      });

      return {
        id: doc.id,
        title: doc.title,
        deal: doc.deal,
        documentType: doc.extractedDocumentTerm?.documentType || '-',
        classification: doc.extractedDocumentTerm?.classification || '-',
        tenantBuyer: extractValue(termsData, 'tenant_name') || extractValue(termsData, 'buyer_name'),
        landlordSeller: extractValue(termsData, 'landlord_name') || extractValue(termsData, 'seller_name'),
        propertyAddress: extractValue(termsData, 'property_address'),
        squareFeet: extractValue(termsData, 'rentable_square_feet') || extractValue(termsData, 'total_square_feet'),
        extractedDate: doc.extractedDocumentTerm?.termsExtractedAt
          ? dayjs(doc.extractedDocumentTerm.termsExtractedAt).format('MMM D, YYYY')
          : '-',
        isExpanded: expandedRowIds.has(doc.id),
        subRows: hasAmendments ? subRows : [],
        originalDocument: doc,
        onRowClick: handleTableRowClick,
      };
    });
  }, [filteredDocuments, expandedRowIds, handleTableRowClick]);

  // Track if we have valid field metadata (memoized to prevent recreating columns)
  const hasFieldMetadata = useMemo(
    () => fieldMetadata && Object.keys(fieldMetadata).length > 0,
    [fieldMetadata]
  );

  // Get column configurations - dynamic generation from field metadata
  const COLUMN_CONFIGS = useMemo(() => {
    const isLease = selectedDocumentType === 'lease';

    // Base columns that are always present (not in field metadata)
    // Note: We don't include expandedRowIds/setExpandedRowIds in Cell definitions here
    // because they're passed separately to the table component
    const baseColumns: ColumnConfig[] = [
      {
        id: 'title',
        header: 'Document Name',
        accessor: 'title',
        defaultVisible: true,
        isSticky: true,
        minWidth: 250,
        className: 'document-name-column',
        documentTypes: ['lease', 'psa'],
        // Cell will be set up separately to avoid dependency issues
        Cell: DocumentNameCell,
      },
      {
        id: 'tenantBuyer',
        header: isLease ? 'Tenant Name' : 'Buyer Name',
        accessor: 'tenantBuyer',
        defaultVisible: true,
        width: 280,
        minWidth: 250,
        className: 'tenant-buyer-column',
        documentTypes: ['lease', 'psa'],
        Cell: ({ value }: any) => <div>{value || '-'}</div>,
      },
      {
        id: 'landlordSeller',
        header: isLease ? 'Landlord Name' : 'Seller Name',
        accessor: 'landlordSeller',
        defaultVisible: true,
        width: 280,
        minWidth: 250,
        className: 'landlord-seller-column',
        documentTypes: ['lease', 'psa'],
        Cell: ({ value }: any) => <div>{value || '-'}</div>,
      },
    ];

    // If field metadata is loaded, generate all columns dynamically
    if (hasFieldMetadata) {
      return generateColumnsFromMetadata(fieldMetadata, selectedDocumentType, baseColumns);
    }

    // Fallback to static columns if metadata not loaded yet
    // Note: We pass expandedRowIds/setExpandedRowIds here for the fallback case
    // but this shouldn't be called once metadata loads
    return getColumnConfigs({ isLease, expandedRowIds, setExpandedRowIds });
  }, [selectedDocumentType, hasFieldMetadata, fieldMetadata]);

  // Column visibility state - reset when document type changes
  const [visibleColumnIds, setVisibleColumnIds] = useState<Set<string>>(new Set());

  // Reset visible columns when document type or COLUMN_CONFIGS changes
  React.useEffect(() => {
    setVisibleColumnIds(new Set(COLUMN_CONFIGS.filter(c => c.defaultVisible).map(c => c.id)));
  }, [COLUMN_CONFIGS]);

  // Define table columns - filter by visibility and map to react-table format
  const columns = useMemo(
    () => COLUMN_CONFIGS
      .filter(config => visibleColumnIds.has(config.id))
      .map(config => {
        // Special handling for title column to inject expandedRowIds props
        const CellComponent = config.id === 'title'
          ? (props: any) => (
              <DocumentNameCell
                {...props}
                expandedRowIds={expandedRowIds}
                setExpandedRowIds={setExpandedRowIds}
              />
            )
          : config.Cell;

        return {
          id: config.id,
          Header: config.header,
          accessor: config.accessor,
          Cell: CellComponent,
          width: config.width,
          minWidth: config.minWidth,
          maxWidth: config.maxWidth,
          disableSortBy: config.disableSortBy,
          className: config.className, // Pass className for CSS targeting
        };
      }),
    [COLUMN_CONFIGS, visibleColumnIds, expandedRowIds, setExpandedRowIds]
  );

  // Initialize react-table
  const table = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: [{ id: 'documentName', desc: false }],
        pageSize: 50,
        pageIndex: 0,
      },
      autoResetExpanded: false, // Prevent auto-reset of expanded rows on data changes
      autoResetSortBy: false,   // Prevent auto-reset of sort state on data changes
      autoResetPage: false,     // Prevent auto-reset of page on data changes
    } as any,
    useSortBy,
    useExpanded,
    usePagination
  );

  // Early return renders AFTER all hooks
  if (restrictionsLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!canCreateTemplates) {
    return (
      <>
        <Helmet>
          <title>Data - Upgrade Required</title>
        </Helmet>
        <SubscriptionRequiredModal
          isOpen={true}
          onClose={() => setShowSubscriptionModal(false)}
        />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Document Data</title>
      </Helmet>

      <ContentHeader>
        <div className={classNames(styles.header, 'ml-3')}>
          <span>Document Data</span>
        </div>
      </ContentHeader>

      <ContentBody className={styles.contentBodyFull}>
        {/* Mobile Search and Filter Row */}
        <div className="mobile">
          <div className={styles.mobileSearchContainer}>
            <SearchInput
              className={styles.mobileSearch}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search documents..."
            />
            <button
              className={styles.mobileFilterButton}
              onClick={() => setShowFilterSlideout(true)}
              aria-label="Open filters"
            >
              <FilterIcon />
              {activeFilters.length > 0 && (
                <span className={styles.mobileFilterBadge}>{activeFilters.length}</span>
              )}
            </button>
          </div>
        </div>

        {/* Desktop Search and Filter Row */}
        <div className="mb-3 non-mobile mx-3">
          <div className={classNames(styles.documentButtons, 'd-flex justify-content-between align-items-center')}>
            {/* Left side: Search, Document Type, and Filter */}
            <div className="d-flex align-items-center">
              <SearchInput
                className={classNames(styles.searchInboxBox, 'mr-2')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search documents, parties, addresses..."
              />

              <Dropdown className="mr-2">
                <Dropdown.Toggle
                  id="dropdown-document-type"
                  as={DropdownToggle}
                >
                  {selectedDocumentType === 'lease' ? 'Lease' : 'PSA'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <DropdownBody>
                    <DropdownItem highlightable>
                      <div
                        onClick={() => {
                          setSelectedDocumentType('lease');
                          setActiveFilters([]);
                        }}
                        style={{ padding: '0.75rem', cursor: 'pointer' }}
                      >
                        Lease
                      </div>
                    </DropdownItem>
                    <DropdownItem highlightable>
                      <div
                        onClick={() => {
                          setSelectedDocumentType('psa');
                          setActiveFilters([]);
                        }}
                        style={{ padding: '0.75rem', cursor: 'pointer' }}
                      >
                        PSA
                      </div>
                    </DropdownItem>
                  </DropdownBody>
                </Dropdown.Menu>
              </Dropdown>

              <Button
                variant="primary"
                className={`mr-2 ${styles.filterButton}`}
                onClick={() => setShowFilterSlideout(true)}
                style={{ position: 'relative', overflow: 'visible' }}
              >
                <FilterIcon width={20} height={20} />
                {activeFilters.length > 0 && (
                  <span className={styles.filterBadge}>{activeFilters.length}</span>
                )}
              </Button>
            </div>

            {/* Right side: Export and Column Visibility */}
            <div className="d-flex align-items-center">
              <Button
                variant="success"
                disabled={tableData.length === 0}
                className="mr-2"
                onClick={() => {
                  const visibleColumns = COLUMN_CONFIGS.filter(config => visibleColumnIds.has(config.id));
                  exportToCSV(tableData, visibleColumns, selectedDocumentType);
                }}
              >
                Export
              </Button>

              <Dropdown>
                <Dropdown.Toggle
                  id="dropdown-columns"
                  as={DropdownToggle}
                >
                  Columns
                </Dropdown.Toggle>
                <Dropdown.Menu align="right" as="div">
                  <DropdownBody>
                    {sortColumnsForDropdown(COLUMN_CONFIGS, visibleColumnIds).map((config, index, array) => {
                      // Check if this is the first inactive column (divider position)
                      const isFirstInactive = index > 0 &&
                        visibleColumnIds.has(array[index - 1].id) &&
                        !visibleColumnIds.has(config.id);

                      return (
                        <React.Fragment key={config.id}>
                          {/* Add divider between active and inactive columns */}
                          {isFirstInactive && (
                            <div className={styles.columnDivider} />
                          )}
                          <DropdownItem
                            highlightable
                            onMouseDown={(e) => {
                              // Prevent focus change which causes scroll repositioning
                              e.preventDefault();
                            }}
                          >
                            <Checkbox
                              id={`column-${config.id}`}
                              checked={visibleColumnIds.has(config.id)}
                              disabled={config.id === 'title'}
                              onChange={(e) => {
                                if (config.id === 'title') return; // Document Name cannot be removed
                                const newSet = new Set(visibleColumnIds);
                                if (e.target.checked) {
                                  newSet.add(config.id);
                                } else {
                                  newSet.delete(config.id);
                                }
                                setVisibleColumnIds(newSet);
                              }}
                              label={config.header}
                            />
                          </DropdownItem>
                        </React.Fragment>
                      );
                    })}
                  </DropdownBody>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>

        {/* Scrollable Table Container */}
        <div className={`${styles.scrollableContainer} non-mobile`}>
          <Row>
            <Col>
              {/* Loading State */}
              {loading && (
                <div className="d-flex justify-content-center py-5">
                  <LoadingSpinner />
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="alert alert-danger" role="alert">
                  Failed to load document data. Please try again.
                </div>
              )}

              {/* Empty State */}
              {!loading && !error && tableData.length === 0 && (
                <div className={styles.emptyState}>
                  <DatabaseIcon className={styles.emptyStateIcon} />
                  {/* Check if filters or search are active - if so, show "no results" message */}
                  {activeFilters.length > 0 || searchText ? (
                    <>
                      <h3>No Matching Results</h3>
                      <p>No documents match your current search or filters. Try adjusting your criteria.</p>
                    </>
                  ) : documents.length === 0 ? (
                    <>
                      <h3>No Extracted Data Yet</h3>
                      <p>Extracted data will appear here once processed.</p>
                    </>
                  ) : (
                    <>
                      <h3>No Matching Results</h3>
                      <p>No documents match your current search. Try adjusting your criteria.</p>
                    </>
                  )}
                </div>
              )}

              {/* Data Table - Standard Table Component */}
              {!loading && !error && tableData.length > 0 && (
                <Table
                  table={table}
                  striped
                  isStickyHeader
                  isStickyFirstColumn={false}
                  renderInlineSubRows
                  paginationBelow
                  isStickyPagination
                />
              )}
            </Col>
          </Row>
        </div>

        {/* Mobile Document List */}
        <div className={styles.mobileTableContainer}>
          {loading ? (
            <div className={styles.mobileLoading}>
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className={styles.mobileEmptyState}>
              <DatabaseIcon />
              <h3>Error Loading Data</h3>
              <p>Please try again later.</p>
            </div>
          ) : tableData.length === 0 ? (
            <div className={styles.mobileEmptyState}>
              <DatabaseIcon />
              {activeFilters.length > 0 || searchText ? (
                <>
                  <h3>No Matching Results</h3>
                  <p>Try adjusting your search or filters.</p>
                </>
              ) : (
                <>
                  <h3>No Extracted Data Yet</h3>
                  <p>Extracted data will appear here once processed.</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className={styles.mobileListArea}>
                {(table as any).page?.map((row: any) => {
                  const doc = row.original?.originalDocument || row.original;
                  return (
                    <div
                      key={doc.id}
                      className={styles.mobileDocumentCard}
                      onClick={() => handleRowClick(doc)}
                    >
                      <p className={styles.mobileDocumentTitle}>{doc.title}</p>
                      <p className={styles.mobileDocumentMeta}>
                        {doc.deal?.title || 'No deal'}
                      </p>
                    </div>
                  );
                })}
              </div>
              <MobilePagination
                pageIndex={(table as any).state.pageIndex}
                pageCount={(table as any).pageCount}
                canPreviousPage={(table as any).canPreviousPage}
                canNextPage={(table as any).canNextPage}
                gotoPage={(table as any).gotoPage}
                previousPage={(table as any).previousPage}
                nextPage={(table as any).nextPage}
                totalItems={tableData.length}
                pageSize={(table as any).state.pageSize}
              />
            </>
          )}
        </div>
      </ContentBody>

      {/* Extracted Terms Slideout */}
      {selectedDocument && selectedDocument.extractedDocumentTerm && (
        <ExtractedTermsSlideout
          isOpen={true}
          onClose={() => setSelectedDocument(null)}
          documentId={selectedDocument.id}
          documentName={selectedDocument.title}
          extractedTerm={selectedDocument.extractedDocumentTerm}
          canEdit={true}
          dealId={selectedDocument.deal.id}
          teamId={teamId}
          documentType={selectedDocumentType}
          onLinkAmendment={handleLinkAmendment}
        />
      )}

      {/* Filter Slideout */}
      {selectedDocumentType && (
        <FilterSlideout
          isOpen={showFilterSlideout}
          onClose={() => setShowFilterSlideout(false)}
          documentType={selectedDocumentType}
          initialFilters={activeFilters}
          onApplyFilters={(filters) => {
            setActiveFilters(filters);
            setShowFilterSlideout(false);
          }}
        />
      )}

      {/* Link Amendment Modal */}
      {amendmentToLink && (
        <LinkAmendmentModal
          isOpen={showLinkAmendmentModal}
          onClose={() => {
            setShowLinkAmendmentModal(false);
            setAmendmentToLink(null);
          }}
          amendmentId={amendmentToLink.id}
          amendmentTitle={amendmentToLink.title}
          dealId={amendmentToLink.dealId}
          documentId={amendmentToLink.documentId}
          teamId={teamId}
          documentType={selectedDocumentType}
          onSuccess={() => {
            setShowLinkAmendmentModal(false);
            setAmendmentToLink(null);
          }}
        />
      )}
    </>
  );
};

export default DataPage;
